import classNames from 'classnames';
import PropTypes from 'prop-types';
import { forwardRef, HTMLAttributes, ReactNode } from 'react';
import Validation from './Validation';

interface GroupedOption {
  value: string | number;
  text: string | number;
}

interface Group {
    label: string;
    options: {
      value: string | number;
      text: string | number;
    }[];
  }

interface IGroupedSelectProps extends HTMLAttributes<HTMLSelectElement> {
  id?: string;
  className?: string;
  name?: string;
  ariaLabel: string;
  placeholder?: string;
  size?: 'lg' | 'sm';
  multiple?: boolean;
  disabled?: boolean;
  required?: boolean;
  ariaDescribedby?: string;
  ariaLabelledby?: string;
  title?: string;
  value?: string | string[];
  defaultValue?: string | string[];
  isTouched?: boolean;
  isValid?: boolean;
  invalidFeedback?: string;
  validFeedback?: string;
  isValidMessage?: boolean;
  isTooltipFeedback?: boolean;
  list?: Group[];
  children?: ReactNode;
  onBlur?(...args: unknown[]): unknown;
  onChange?(...args: unknown[]): unknown;
  onFocus?(...args: unknown[]): unknown;
  onInput?(...args: unknown[]): unknown;
  onInvalid?(...args: unknown[]): unknown;
  onSelect?(...args: unknown[]): unknown;
}

const GroupedSelect = forwardRef<HTMLSelectElement, IGroupedSelectProps>(
  (
    {
      id,
      name,
      className,
      placeholder,
      ariaDescribedby,
      ariaLabelledby,
      ariaLabel,
      size,
      multiple,
      disabled,
      title,
      value,
      defaultValue,
      required,
      isTouched,
      isValid,
      invalidFeedback,
      validFeedback,
      isValidMessage,
      isTooltipFeedback,
      list,
      children,
      onBlur,
      onChange,
      onFocus,
      onInput,
      onInvalid,
      onSelect,
      ...props
    },
    ref,
  ) => {
    return (
      <>
        <select
          ref={ref}
          id={id}
          className={classNames(
            'form-select',
            {
              [`form-select-${size}`]: size,
              'text-muted': value === '' && placeholder,
              'is-invalid': !isValid && isTouched && invalidFeedback,
              'is-valid': !isValid && isTouched && !invalidFeedback,
            },
            className,
          )}
          name={name}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedby}
          aria-labelledby={ariaLabelledby}
          multiple={multiple}
          disabled={disabled}
          title={title}
          value={value}
          defaultValue={defaultValue}
          required={required}
          onBlur={onBlur}
          onChange={onChange}
          onFocus={onFocus}
          onInput={onInput}
          onInvalid={onInvalid}
          onSelect={onSelect}
          {...props}>
          {placeholder && (
            <option value="" hidden>
              {placeholder}
            </option>
          )}

          {children ||
            (list &&
              list.map((group, i) => (
                <optgroup key={`group-${i}`} label={group.label}>
                  {group.options.map((opt, j) => (
                    <option key={`opt-${i}-${j}`} value={opt.value}>
                      {opt.text}
                    </option>
                  ))}
                </optgroup>
              )))}
        </select>
        {isValidMessage && (
          <Validation
            isTouched={isTouched}
            invalidFeedback={invalidFeedback}
            validFeedback={validFeedback}
            isTooltip={isTooltipFeedback}
          />
        )}
      </>
    );
  },
);

GroupedSelect.displayName = 'GroupedSelect';

GroupedSelect.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  name: PropTypes.string,
  ariaLabel: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  size: PropTypes.oneOf(['lg', 'sm']),
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  ariaDescribedby: PropTypes.string,
  ariaLabelledby: PropTypes.string,
  title: PropTypes.string,
  // @ts-ignore
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  // @ts-ignore
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  list: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
          text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        }).isRequired
      ).isRequired,
    }).isRequired
  ),
  children: PropTypes.node,
  isTouched: PropTypes.bool,
  isValid: PropTypes.bool,
  invalidFeedback: PropTypes.string,
  validFeedback: PropTypes.string,
  isValidMessage: PropTypes.bool,
  isTooltipFeedback: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onInput: PropTypes.func,
  onInvalid: PropTypes.func,
  onSelect: PropTypes.func,
};

GroupedSelect.defaultProps = {
  id: undefined,
  className: undefined,
  name: undefined,
  children: null,
  placeholder: undefined,
  multiple: false,
  size: undefined,
  disabled: false,
  required: false,
  ariaDescribedby: undefined,
  ariaLabelledby: undefined,
  title: undefined,
  value: undefined,
  defaultValue: undefined,
  list: undefined,
  isTouched: false,
  isValid: false,
  invalidFeedback: undefined,
  validFeedback: undefined,
  isValidMessage: true,
  isTooltipFeedback: false,
  onBlur: undefined,
  onChange: undefined,
  onFocus: undefined,
  onInput: undefined,
  onInvalid: undefined,
  onSelect: undefined,
};

export default GroupedSelect;
